<template>
  <b-card-code
    no-body
  >
    <b-modal
      id="modalDelete"
      ref="modalDelete"
      :title="`${recordToDelete.isPermanent ? 'Are you sure you want to permanently delete this record?' : 'Are you sure you want to delete this record?'}`"
      centered
      ok-title="Delete"
      @ok="deleteModalHandler"
    >
      <div>
        {{ recordToDelete.isPermanent ? 'Permanently deleted records cannot be recovered later.' : 'In case you might want to recover it later, you can enable the "With deleted" toggle to find it and either recover or permanently delete it.' }}
      </div>
    </b-modal>
    <b-row
      class="px-2 mb-2"
    >
      <b-col
        v-if="$can('create', 'sites')"
        order-md="2"
        order="1"
        class="text-right d-flex align-items-end justify-content-end"
      >
        <div
          v-if="(searchFields && searchFields.length > 0) || (orderBy && orderBy.length > 0) || (sortedBy && sortedBy.length > 0)"
          class="d-flex justify-content-end align-items-center mr-2"
        >
          <b-button
            variant="primary"
            class="reset-filters-button"
            @click="handleReset"
          >
            <feather-icon
              icon="XIcon"
              size="12"
              stroke="#f0554e"
              class="text-body align-middle mr-25"
            />
            Reset filters
          </b-button>
        </div>
        <b-button
          to="/sites/create"
          variant="primary"
        >
          Create
        </b-button>
        <b-button
          v-b-tooltip.hover
          variant="link"
          class="ml-2 pl-1 pr-1"
          title="Refresh statuses"
          @click="refreshSitesStatus"
        >
          <feather-icon
            icon="RefreshCcwIcon"
            size="18"
            :class="{ 'spinner-icon': isRefreshing }"
          />
        </b-button>
        <b-button
          v-if="$can('additional-action-delete', 'sites')"
          variant="primary"
          class="ml-2"
          @click="toggleFilterOnlyTrashed"
        >
          <span v-if="onlyTrashed">Without deleted</span>
          <span v-else>Only deleted</span>
        </b-button>
        <div
          v-if="$can('delete', 'sites')"
          label-class="label-class"
          class="with-deleted-checkbox ml-2"
        >
          <span>With deleted</span>
          <b-form-checkbox
            class="mr-0"
            switch
            inline
            @change="setFilterWithTrashed"
          />
        </div>
      </b-col>

      <b-col
        md="6"
        order-md="1"
        order="2"
      >
        <b-form @submit.prevent="searchRows()">
          <p v-if="companyId">
            Company: <a
              :href="`/company-management/summary/${companyId}`"
            >
              {{ companyName }}
            </a>
          </p>
        </b-form>
      </b-col>
    </b-row>
    <table-mobile-solo
      :search="searchValue"
      :handle-search="handleMobileSearch"
      :mobile-search-fields="mobileSearchFields"
      :loading="isLoading"
      :items="items"
      :fields="fields"
      :pagination="{
        pagination: pagination,
        perPage: perPage,
        currentPage: page,
        pageSizeOptions: pageSizeOptions,
        enabled: true,
        perPageChanged: perPageChanged,
        changePage: changePage,
      }"
      :rules="{
        country(value) {
          return value.addresses.country ? value.addresses.country.name : '-'
        },
        client(value) {
          return value.company ? value.company.name : '-'
        },
        city(value) {
          return value.addresses ? value.addresses.city : '-'
        },
        zipCode(value) {
          return value.addresses ? value.addresses.postcode : '-'
        },
      }"
      :key-value="'name'"
      :actions="[{
        label: 'View',
        type: 'primary',
        icon: 'EyeIcon',
        visible: () => true,
        click: ({id}) => this.$router.push({
          name: 'site-summary',
          params: {
            id: +id,
          },
        }),
      },{
        label: 'Edit Contact Info',
        type: 'primary',
        icon: 'Edit2Icon',
        visible: () => $can('view', 'sites'),
        click: ({id}) => redirectToEdit(id),
      }, {
        label: 'Delete',
        type: 'danger',
        icon: 'TrashIcon',
        visible: ({is_deleted}) => !is_deleted && $can('delete', 'sites'),
        click: ({id}) => deleteHandler(id),
      }, {
        label: 'Permanently delete',
        type: 'danger',
        icon: 'TrashIcon',
        visible: ({is_deleted}) => is_deleted && $can('delete', 'sites'),
        click: ({id}) => deleteHandler(id, true),
      }, {
        label: 'Recover',
        type: 'primary',
        icon: 'RefreshCcwIcon',
        visible: ({is_deleted}) => is_deleted && $can('delete', 'sites'),
        click: ({id}) => recoverRow(id),
      }]"
    />

    <b-card-body class="no-padding-table">

      <!-- table -->
      <vue-good-table
        ref="VueGoodTable"
        :is-loading="isLoading"
        :columns="fields"
        :rows="items"
        :total-rows="pagination.total"
        class="sites-table hide-mobile-table"
        :row-style-class="rowStyleClassFn"
        mode="remote"
        compact-mode
        :sort-options="{
          enabled: true,
          multipleColumns: true,
          initialSortBy: {field: orderBy, type: sortedBy}
        }"
        :pagination-options="{
          enabled: true,
          perPage: perPage,
          setCurrentPage: page
        }"
        @on-column-filter="columnFilterFn"
        @on-sort-change="onSortChange"
      >
        <template slot="loadingContent">
          <b-spinner
            variant="primary"
            label="Spinning"
          />
        </template>
        <template
          slot="table-row"
          slot-scope="props"
        >
          <span v-if="props.column.field === 'action'">
            <span>
              <b-dropdown
                variant="link"
                toggle-class="text-decoration-none"
                no-caret
              >
                <template v-slot:button-content>
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="16"
                    class="text-body align-middle mr-25"
                  />
                </template>
                <b-dropdown-item
                  @click="redirectToSite(props.row.id)"
                >
                  <feather-icon
                    icon="EyeIcon"
                    class="mr-50"
                  />
                  <span>Manage Site</span>
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="$can('view', 'sites')"
                  @click="redirectToEdit(props.row.id)"
                >
                  <feather-icon
                    icon="Edit2Icon"
                    class="mr-50"
                  />
                  <span>Edit Contact Info</span>
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="!props.row.is_deleted && $can('delete', 'sites')"
                  @click.prevent="deleteHandler(props.row.id)"
                >
                  <feather-icon
                    icon="TrashIcon"
                    class="mr-50"
                  />
                  <span>Delete</span>
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="props.row.is_deleted && $can('delete', 'sites')"
                  @click.prevent="deleteHandler(props.row.id, true)"
                >
                  <feather-icon
                    icon="TrashIcon"
                    class="mr-50"
                  />
                  <span>Permanently delete</span>
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="props.row.is_deleted && $can('delete', 'sites')"
                  @click.prevent="recoverRow(props.row.id)"
                >
                  <feather-icon
                    icon="RefreshCcwIcon"
                    class="mr-50"
                  />
                  <span>Recover</span>
                </b-dropdown-item>
              </b-dropdown>
            </span>
          </span>
          <span v-if="props.column.field === 'is_online'">
            <template>
              <b-badge
                v-if="siteRefreshStatus[props.row.id] === 'loading'"
                pill
                variant="secondary"
                class="mr-1"
              >{{ props.row.is_online ? 'Online' : 'Offline' }}</b-badge>
              <b-badge
                v-else-if="!props.row.is_online"
                pill
                variant="danger"
                class="mr-1"
              >Offline</b-badge>
              <b-badge
                v-else
                pill
                variant="success"
                class="mr-1"
              >Online</b-badge>
            </template>
            <span v-if="siteRefreshStatus[props.row.id] === 'loading'">
              <feather-icon
                icon="LoaderIcon"
                size="16"
                class="spinner-icon"
              />
            </span>
            <span
              v-if="siteRefreshStatus[props.row.id] === 'failed'"
              v-b-tooltip.hover
              title="Failed to refresh"
            >
              <feather-icon
                icon="XCircleIcon"
                size="16"
                class="text-danger"
              />
            </span>
          </span>
          <span v-else-if="props.column.field === 'name'">
            <router-link :to="`/site/${props.formattedRow.id}/hardware/index`">
              {{ props.formattedRow.name }}
            </router-link>
          </span>
          <span v-else-if="props.column.field === 'status'">
            <b-badge
              v-if="props.formattedRow.status !== '-'"
              pill
              :variant="getStatusColor(props.formattedRow.status)"
            >
              <span
                v-if="props.formattedRow.status"
                class="text-white text-capitalize"
              >
                {{ props.formattedRow.status }}
              </span>
            </b-badge>
          </span>
          <span v-else-if="props.column.field === 'company'">
            {{ props.formattedRow.company }}
          </span>
          <span v-else>
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>

        <!-- pagination -->
        <template
          slot="pagination-bottom"
        >
          <div class="d-flex justify-content-between flex-wrap">
            <div class="d-flex align-items-center mb-0 mt-1">
              <span class="text-nowrap">
                Showing 1 to
              </span>
              <b-form-select
                v-model="perPage"
                :options="pageSizeOptions"
                class="mx-1"
                @change="perPageChanged"
              />
              <span class="text-nowrap"> of {{ pagination.total }} entries </span>
            </div>
            <div>
              <b-pagination
                v-if="pagination.last_page !== 1"
                :value="page"
                :total-rows="pagination.total"
                :per-page="perPage"
                first-number
                last-number
                align="right"
                prev-class="prev-item"
                next-class="next-item"
                class="mt-1 mb-0"
                @change="changePage"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>
            </div>
          </div>
        </template>
      </vue-good-table>
    </b-card-body>
    <b-modal
      id="modalSoftDelete"
      ref="modalSoftDelete"
      :title="deleteErrorPopUpInfo.title"
      centered
      ok-only
      ok-variant="secondary"
      ok-title="Cancel"
    >
      <p v-html="deleteErrorPopUpInfo.content" />
    </b-modal>
  </b-card-code>
</template>

<script>
import BCardCode from '@core/components/b-card-code/BCardCode.vue'
import {
  BCardBody,
  BPagination,
  BButton,
  BRow,
  BCol,
  BFormSelect,
  BForm,
  BSpinner,
  VBTooltip,
  BBadge,
  BModal,
  BDropdown,
  BDropdownItem,
  BFormCheckbox,
} from 'bootstrap-vue'
import { mapActions, mapMutations, mapState } from 'vuex'
import IndexPagesMixin from '@/mixins/IndexPages.vue'
import { VueGoodTable } from 'vue-good-table'
import { ref, watch } from '@vue/composition-api'
import TableMobileSolo from '@/components/TableMobileSolo.vue'
import axiosIns from '../axios-resolver'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    TableMobileSolo,
    BFormCheckbox,
    BCardCode,
    BCardBody,
    BButton,
    BPagination,
    BRow,
    BCol,
    BModal,
    BFormSelect,
    BForm,
    BSpinner,
    BBadge,
    BDropdown,
    BDropdownItem,
    VueGoodTable,
  },
  mixins: [
    IndexPagesMixin,
  ],
  data() {
    return {
      searchValue: '',
      isLoading: true,
      uppdateRouteName: 'site-update',
      currentRoute: this.$route.path,

      recordToDelete: {
        id: null,
        isPermanent: false,
      },

      deleteErrorPopUpInfo: {},

      siteRefreshStatus: {},

      mobileSearchFields: {
        name: '',
        'company.name': '',
      },
      fields: [
        {
          field: 'id',
          label: 'Id',
          hidden: true,
        },
        {
          field: 'name',
          label: 'Site Name',
          filterOptions: {
            enabled: true,
            placeholder: 'Filter',
            filterValue: '',
          },
        },
        {
          field: 'company.name',
          label: 'Company',
          hidden: JSON.parse(localStorage.getItem('userData')).roles.includes('client'),
          mobileSpecial: 'client',
          sortable: true,
          filterOptions: {
            enabled: true,
            placeholder: 'Filter',
            filterValue: '',
          },
        },
        {
          field: 'contact_name',
          label: 'Contact name',
          sortable: true,
          filterOptions: {
            enabled: true,
            placeholder: 'Filter',
            filterValue: '',
          },
        },
        {
          field: 'addresses.postcode',
          label: 'Zip / Post Code',
          mobileSpecial: 'zipCode',
          sortable: false,
          filterOptions: {
            enabled: true,
            placeholder: 'Filter',
            filterValue: '',
          },
        },
        {
          field: 'addresses.city',
          label: 'Town / City',
          sortable: true,
          mobileSpecial: 'city',
          filterOptions: {
            enabled: true,
            placeholder: 'Filter',
            filterValue: '',
          },
        },
        {
          field: 'is_online',
          label: 'Controller Status',
          filterOptions: {
            enabled: true,
            placeholder: 'Status',
            filterValue: '',
          },
        },
        {
          field: 'status',
          label: 'Licence Status',
          filterOptions: {
            enabled: true,
            placeholder: 'Status',
            filterValue: '',
          },
        },
        {
          field: 'action',
          label: 'Action',
          sortable: false,
          thClass: 'action-th-width',
          hidden: !this.$can('update', 'hardware') && !this.$can('delete', 'hardware'),
        },
      ],
    }
  },
  setup() {
    const companyName = ref('')
    const companyId = ref('')

    watch(companyName, companyId, () => {
      if (this.$route.query.companyId) {
        const company = this.getCompany(this.$route.query.companyId)
        this.companyId = this.$route.query.companyId
        this.companyName = company.name
      } else {
        this.companyName = ''
      }
    })
    return {
      companyName: '',
    }
  },
  computed: {
    ...mapState('sites', {
      items: 'items',
      pagination: 'pagination',
      statuses: 'statuses',
      hardwareStatuses: 'hardwareStatuses',
      searchFields: 'searchFields',
      orderBy: 'orderBy',
      sortedBy: 'sortedBy',
    }),
    page() {
      return this.$route.query?.params || {}
    },
    isRefreshing() {
      return Object.values(this.siteRefreshStatus).some(status => status === 'loading')
    },
  },
  watch: {
    async $route(to) {
      const additionalPathname = to.fullPath.replace(to.path, '')
      if (!additionalPathname) {
        await this.clearAllData()
        this.getData()
        this.companyName = ''
        this.companyId = ''
      }
    },

    recordToDelete(val) {
      if (val.id) {
        this.$refs.modalDelete.show()
      } else {
        this.$refs.modalDelete.hide()
      }
    },

    items() {
      this.fields = this.fields.map(item => {
        if (item.label === 'Controller Status') {
          // eslint-disable-next-line no-param-reassign
          item = { ...item, filterOptions: { ...item.filterOptions, filterDropdownItems: [...this.statuses] } }
        }
        if (item.label === 'Licence Status') {
          // eslint-disable-next-line no-param-reassign
          item = { ...item, filterOptions: { ...item.filterOptions, filterDropdownItems: [...this.hardwareStatuses] } }
        }
        return item
      })
    },
  },
  async mounted() {
    if (this.$route.query.companyId) {
      const company = await this.getCompany(this.$route.query.companyId)
      this.companyName = company.name
    }
  },
  created() {
    const userData = JSON.parse(localStorage.getItem('userData'))
    if (userData.roles.includes('client')) {
      this.fields = this.fields.filter(item => item.field !== 'company')
    }
  },
  methods: {
    ...mapActions('sites', {
      fetchItems: 'getItems',
      deleteRecord: 'deleteItem',
      setSearchFields: 'setSearchFields',
      setOrderBy: 'setOrderBy',
      setSortedBy: 'setSortedBy',
      getCompany: 'getCompany',
      recoverRecord: 'recoverItem',
    }),
    ...mapMutations('sites', ['RESET_STATE', 'UPDATE_ITEM']),
    async deleteHandler(id, isPermanent = false) {
      this.recordToDelete = {
        id,
        isPermanent,
      }
    },
    handleReset() {
      this.resetFilter()
      this.searchValue = ''
    },
    async handleMobileSearch(search, searchValue) {
      if (this.search !== searchValue) {
        this.searchValue = searchValue
      }

      this.columnFilterFn(search, true)
    },
    async deleteModalHandler() {
      const errorRes = await this.deleteRow(this.recordToDelete.id)
      this.deleteErrorPopUpInfo = errorRes
      this.recordToDelete = {
        id: null,
        isPermanent: false,
      }
    },
    redirectToSite(id) {
      this.$router.push(`/site/${id}/hardware/index`)
    },
    redirectToEdit(id) {
      this.$router.push(`/sites/update/${id}`)
    },
    rowStyleClassFn(row) {
      return row.is_deleted ? 'deleted-class' : ''
    },
    async refreshSitesStatus() {
      const siteIds = this.items.map(item => item.id)

      siteIds.forEach(id => {
        this.$set(this.siteRefreshStatus, id, 'loading')
      })

      const requests = siteIds.map(siteId => axiosIns.post('/site/refreshAll', { ids: [siteId] })
        .then(response => {
          // SUCCESS
          this.$set(this.siteRefreshStatus, siteId, 'success')

          if (response.data && response.data.data && response.data.data.length > 0) {
            const updatedSite = response.data.data[0]
            const formattedSite = {
              ...updatedSite,
              telephone: `${updatedSite.phone_country_code ? `+${updatedSite.phone_country_code}` : ''}${updatedSite.phone_number ?? ''}`,
            }
            this.UPDATE_ITEM(formattedSite)
          }
        })
        .catch(() => {
          // FAILED
          this.$set(this.siteRefreshStatus, siteId, 'failed')
        }))

      await Promise.allSettled(requests)
    },
    getStatusColor(statusId) {
      let colorVariant
      switch (statusId) {
        case 'un-allocated':
          colorVariant = 'info'
          break
        case 'allocated':
          colorVariant = 'success'
          break
        case 'activated':
          colorVariant = 'success'
          break
        case 'active':
          colorVariant = 'success'
          break
        case 'renewed':
          colorVariant = 'success'
          break
        case 'ceasing':
          colorVariant = 'secondary'
          break
        case 'expired':
          colorVariant = 'danger'
          break
        default:
          colorVariant = 'danger'
      }
      return colorVariant
    },
  },
}
</script>

<style lang="scss">
  @import '@core/scss/vue/libs/vue-select.scss';
  .vgt-table{
    .vgt-left-align, .vgt-right-align{
      vertical-align: middle !important;
    }
  }
  .dark-layout{
    .vgt-select{
      background-color: #283046;
      border-color: #404656;
    }
    .table-selected-row-info-panel{
      background-color: #283046;
      border-color: #404656;
    }
  }
  .sites-table {
    .deleted-class {
      .vgt-checkbox-col {
        background-color: rgba(255, 0, 0, 0.185) !important;
          input{
            display: none;
          }
      }
      .vgt-left-align {
        background-color: rgba(255, 0, 0, 0.185) !important;
      }
    }
  }
  .spinner-icon {
    animation: spin 1s linear infinite;
  }
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
</style>
